import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  Switch,
  FormControlLabel,
  Stack,
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import TabHeaderTypography from '../tab_header';

const CATEGORIES_FIXED = [
  'Formation',
  'Compliance',
  'Tax',
  'Support',
  'Address',
  'Banking',
  'Software',
  'Digital',
  'Legal',
  'Accounting',
  'Insurance',
  'Design',
];

const ManageFeatures = ({ allFeatures }) => {
  const [features, setFeatures] = useState(allFeatures);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentFeature, setCurrentFeature] = useState(null);
  const [categories, setCategories] = useState(CATEGORIES_FIXED);

  const handleSave = async () => {
    try {
      if (currentFeature.id) {
        // Update existing feature
        setFeatures(
          features.map((f) => (f.id === currentFeature.id ? currentFeature : f))
        );
      } else {
        // Add new feature
        const newId = (features.length + 1).toString();
        const newFeature = {
          ...currentFeature,
          id: newId,
          active: true,
        };
        setFeatures([...features, newFeature]);

        // Add category if it's new
        if (!categories.includes(newFeature.category)) {
          setCategories([...categories, newFeature.category]);
        }
      }
      setOpenDialog(false);
    } catch (error) {
      console.error('Error saving feature:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this feature?')) {
      try {
        setFeatures(features.filter((f) => f.id !== id));
      } catch (error) {
        console.error('Error deleting feature:', error);
      }
    }
  };

  const toggleFeatureStatus = (id) => {
    setFeatures(
      features.map((f) => (f.id === id ? { ...f, active: !f.active } : f))
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', pb: 2 }}>
        <TabHeaderTypography title="Manage" highlight="Features" />
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setCurrentFeature({
              id: '',
              name: '',
              category: '',
              active: true,
            });
            setOpenDialog(true);
          }}
        >
          Add Feature
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {features.map((feature) => (
              <TableRow key={feature.id}>
                <TableCell>
                  <Typography>{feature.name}</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={feature.category} size="small" />
                </TableCell>
                <TableCell>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={feature.active}
                        onChange={() => toggleFeatureStatus(feature.id)}
                        color="primary"
                      />
                    }
                    label={feature.active ? 'Active' : 'Inactive'}
                  />
                </TableCell>
                <TableCell>
                  <Stack direction={'row'}>
                    <IconButton
                      onClick={() => {
                        setCurrentFeature(feature);
                        setOpenDialog(true);
                      }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(feature.id)}>
                      <Delete color="error" />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {currentFeature?.id ? 'Edit Feature' : 'Create New Feature'}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Feature Name"
            value={currentFeature?.name || ''}
            onChange={(e) =>
              setCurrentFeature({
                ...currentFeature,
                name: e.target.value,
              })
            }
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Category"
            value={currentFeature?.category || ''}
            onChange={(e) =>
              setCurrentFeature({
                ...currentFeature,
                category: e.target.value,
              })
            }
            margin="normal"
            required
            select
            SelectProps={{ native: true }}
          >
            <option value=""></option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </TextField>
          {!currentFeature?.id && (
            <FormControlLabel
              control={
                <Switch
                  checked={currentFeature?.active !== false}
                  onChange={(e) =>
                    setCurrentFeature({
                      ...currentFeature,
                      active: e.target.checked,
                    })
                  }
                  color="primary"
                />
              }
              label="Active"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ManageFeatures;
